// Gemini AI Service for Document Summarization and Performance Badge Evaluation
import { supabase } from '@/integrations/supabase/client';
import { taskbloomSupabase } from '@/integrations/supabase/taskbloom-client';

// ============================================
// Document Summarization Service (Original)
// ============================================

export class GeminiService {
  static async summarizeDocument(fileContent: string, fileName: string): Promise<string> {
    try {
      // Summarization runs server-side in the `summarize-report` edge function so
      // the Gemini API key never reaches the browser. We only send content and
      // receive the finished summary.
      const { data, error } = await supabase.functions.invoke('summarize-report', {
        body: { fileContent, fileName },
      });

      if (error) {
        throw new Error(error.message);
      }
      if (!data?.success || !data?.summary) {
        throw new Error(data?.error || 'No response from summarization service');
      }

      return data.summary as string;
    } catch (error) {
      console.error('Error calling summarize-report:', error);
      throw new Error(`Không thể tạo tóm tắt: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

// ============================================
// Performance Badge Evaluation Service (New)
// ============================================

export interface MemberPerformance {
  member_id: string;
  member_name: string;
  total_tasks: number;
  completed: number;
  in_progress: number;
  todo: number;
  overdue: number;
  avg_hours_per_task: number;
  avg_days_per_task: number;
  completion_rate: number;
  time_filter: 'week' | 'month';
}

export interface PerformanceBadge {
  badge: 'Xuất Sắc' | 'Tốt' | 'Trung Bình' | 'Yếu' | 'Kém';
  icon: '👑' | '⭐' | '📊' | '⚠️' | '🔴';
  color: 'gold' | 'green' | 'blue' | 'orange' | 'red';
  reason: string;
}

// Batch evaluate multiple members via Edge Function
export const batchEvaluatePerformance = async (
  members: MemberPerformance[]
): Promise<Map<string, PerformanceBadge>> => {
  const results = new Map<string, PerformanceBadge>();
  
  if (members.length === 0) {
    return results;
  }

  try {
    const { data, error } = await taskbloomSupabase.functions.invoke('evaluate-performance', {
      body: { members }
    });

    if (error) {
      console.error('Error calling evaluate-performance function:', error);
      throw error;
    }

    // Map results back to member IDs
    for (const member of members) {
      if (data[member.member_id]) {
        results.set(member.member_id, data[member.member_id]);
      }
    }

    return results;
  } catch (error) {
    console.error('Error in batchEvaluatePerformance:', error);
    
    // Fallback: use local evaluation for all members
    for (const member of members) {
      const { completion_rate, overdue, completed, avg_days_per_task } = member;
      
      let badge: PerformanceBadge;
      
      // Không có dữ liệu thời gian → xếp thấp
      const hasTimeData = avg_days_per_task > 0;
      
      // Kém: Rất ít task HOẶC không có dữ liệu thời gian với rất ít task
      if (
        completed < 2 || 
        (!hasTimeData && completed < 5) ||
        (hasTimeData && avg_days_per_task > 10) || 
        overdue > 8
      ) {
        badge = {
          badge: 'Kém',
          icon: '🔴',
          color: 'red',
          reason: !hasTimeData 
            ? `Quá ít công việc (${completed} tasks), không có dữ liệu thời gian, CẦN CẢI THIỆN`
            : `Quá ít công việc (${completed} tasks) hoặc tốc độ rất chậm (TB ${avg_days_per_task.toFixed(1)} ngày), CẦN CẢI THIỆN`
        };
      }
      // Xuất Sắc: Nhiều task, rất nhanh, ít/không trễ (PHẢI có dữ liệu thời gian)
      else if (
        hasTimeData &&
        completed >= 15 && 
        avg_days_per_task <= 1 && 
        overdue <= 2
      ) {
        badge = {
          badge: 'Xuất Sắc',
          icon: '👑',
          color: 'gold',
          reason: `${completed} tasks hoàn thành rất nhanh (TB ${avg_days_per_task.toFixed(2)} ngày), xuất sắc`
        };
      }
      // Tốt: Số lượng tốt, tốc độ tốt HOẶC nhiều task không có thời gian
      else if (
        (hasTimeData && completed >= 5 && avg_days_per_task <= 3 && overdue <= 3) ||
        (!hasTimeData && completed >= 15 && overdue <= 3)
      ) {
        badge = {
          badge: 'Tốt',
          icon: '⭐',
          color: 'green',
          reason: !hasTimeData 
            ? `${completed} tasks hoàn thành, không có dữ liệu thời gian nhưng số lượng tốt`
            : `${completed} tasks hoàn thành, tốc độ tốt (TB ${avg_days_per_task.toFixed(1)} ngày), hiệu suất ổn`
        };
      }
      // Yếu: Ít task hoặc chậm hoặc không có dữ liệu với task ít
      else if (
        completed < 5 || 
        (!hasTimeData && completed < 10) ||
        (hasTimeData && avg_days_per_task > 5) || 
        overdue > 5
      ) {
        badge = {
          badge: 'Yếu',
          icon: '⚠️',
          color: 'orange',
          reason: !hasTimeData 
            ? `Ít công việc (${completed} tasks), không có dữ liệu thời gian, cần cải thiện`
            : `Ít công việc (${completed} tasks) hoặc tốc độ chậm (TB ${avg_days_per_task.toFixed(1)} ngày), cần cải thiện`
        };
      }
      // Trung Bình: Còn lại (task vừa phải hoặc tốc độ vừa)
      else {
        badge = {
          badge: 'Trung Bình',
          icon: '📊',
          color: 'blue',
          reason: !hasTimeData 
            ? `${completed} tasks hoàn thành, không có dữ liệu thời gian, hiệu suất trung bình`
            : `${completed} tasks hoàn thành, tốc độ TB ${avg_days_per_task.toFixed(1)} ngày, hiệu suất trung bình`
        };
      }
      
      results.set(member.member_id, badge);
    }
    
    return results;
  }
};
